/**
 * Atlas AI Help Data
 * Contains categories, example prompts, quick chips, and glossary for Atlas AI assistant
 */

export const ATLAS_HELP_CATEGORIES = [
  {
    id: 'health',
    title: 'Customer & Campaign Health',
    icon: 'heartbeat',
    color: '#ef4444',
    examples: [
      { text: 'How is [customer name] doing?', prompt: 'How is this customer doing?' },
      { text: 'Give me a health check for customer 123', prompt: 'Give me a health check for this customer' },
      { text: 'Which campaigns are ending soon?', prompt: 'Which campaigns are ending soon?' },
      { text: 'Compare campaign A vs campaign B', prompt: 'Compare these campaigns' },
    ],
  },
  {
    id: 'performance',
    title: 'Campaign Performance',
    icon: 'chart-line',
    color: '#3b82f6',
    examples: [
      { text: 'What are the UFI metrics for campaign 123?', prompt: 'What are the UFI metrics for this campaign?' },
      { text: 'Is this campaign on track?', prompt: 'Is this campaign on track?' },
      { text: 'Show me impressions for the last 7 days', prompt: 'Show me impressions for the last 7 days' },
      { text: 'How did this week compare to last week?', prompt: 'How did this week compare to last week?' },
    ],
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    icon: 'bug',
    color: '#f59e0b',
    examples: [
      { text: 'Why did impressions drop?', prompt: 'Why did impressions drop?' },
      { text: 'What caused the dip on [date]?', prompt: 'What caused the recent dip in performance?' },
      { text: 'Should we rematch this campaign?', prompt: 'Should we rematch this campaign?' },
      { text: 'Are there any anomalies?', prompt: 'Are there any anomalies?' },
    ],
  },
  {
    id: 'audience',
    title: 'Audience Insights',
    icon: 'users',
    color: '#8b5cf6',
    examples: [
      { text: 'Who is seeing this campaign?', prompt: 'Who is seeing this campaign?' },
      { text: "What's the demographic profile?", prompt: "What's the demographic profile?" },
      { text: 'Show me audience affinities', prompt: 'Show me audience affinities' },
      { text: 'Which segments are over-indexing?', prompt: 'Which segments are over-indexing?' },
    ],
  },
  {
    id: 'inventory',
    title: 'Inventory & Planning',
    icon: 'map-marker',
    color: '#10b981',
    examples: [
      { text: 'What inventory is available in New York?', prompt: 'What inventory is available?' },
      { text: 'Are there inventory conflicts?', prompt: 'Are there inventory conflicts?' },
      { text: 'Show me top-performing inventory', prompt: 'Show me top-performing inventory' },
    ],
  },
  {
    id: 'attribution',
    title: 'Attribution Studies',
    icon: 'flask',
    color: '#06b6d4',
    examples: [
      { text: 'How many attribution studies did this customer run?', prompt: 'How many attribution studies were run?' },
      { text: 'Show me results for attribution study 456', prompt: 'Show me attribution study results' },
      { text: "What's the lift from this study?", prompt: "What's the lift from this study?" },
      { text: 'Get conversion data by ad group', prompt: 'Get conversion data by ad group' },
    ],
  },
  {
    id: 'learn',
    title: 'Learn & Explore',
    icon: 'graduation-cap',
    color: '#ec4899',
    examples: [
      { text: 'What does LTS mean?', prompt: 'What does LTS mean?' },
      { text: 'Explain reach vs frequency', prompt: 'Explain reach vs frequency' },
      { text: "What's a flight?", prompt: "What's a flight?" },
    ],
  },
];

export const ATLAS_QUICK_PROMPTS = [
  { label: 'Customer health', prompt: 'How is this customer doing?', icon: 'heart' },
  { label: 'Campaign metrics', prompt: 'What are the UFI metrics for this campaign?', icon: 'chart' },
  { label: 'Diagnose dip', prompt: 'Why did impressions drop?', icon: 'search' },
  { label: 'Check pacing', prompt: 'Is this campaign on track?', icon: 'clock' },
  { label: 'Find anomalies', prompt: 'Are there any anomalies?', icon: 'alert' },
  { label: 'Audience breakdown', prompt: 'Show me demographics and affinities', icon: 'users' },
  { label: 'Attribution results', prompt: 'Show me attribution study results', icon: 'flask' },
  { label: 'Explain metric', prompt: 'What does LTS mean?', icon: 'book' },
];

export const ATLAS_TOOLTIP_EXAMPLES = [
  'How is this customer doing?',
  'Why did impressions drop?',
  'What does LTS mean?',
];

export const ATLAS_GLOSSARY = {
  LTS: {
    term: 'LTS',
    definition: 'Likely to See - Highest-confidence impressions',
    category: 'Metrics',
  },
  OTS: {
    term: 'OTS',
    definition: 'Opportunity to See - Medium-confidence impressions',
    category: 'Metrics',
  },
  Gross: {
    term: 'Gross',
    definition: 'All impressions regardless of viewability',
    category: 'Metrics',
  },
  UFI: {
    term: 'UFI',
    definition: 'Uniques, Frequency, Impressions - Core campaign metrics',
    category: 'Metrics',
  },
  Pacing: {
    term: 'Pacing',
    definition: 'Whether a campaign is on schedule to meet its goals',
    category: 'Campaign',
  },
  Flight: {
    term: 'Flight',
    definition: 'An ad group with specific dates and inventory assignments',
    category: 'Campaign',
  },
  Reach: {
    term: 'Reach',
    definition: 'Unique people exposed to the campaign',
    category: 'Metrics',
  },
  Frequency: {
    term: 'Frequency',
    definition: 'Average times each person saw the ad',
    category: 'Metrics',
  },
  Index: {
    term: 'Index',
    definition: 'Performance vs baseline (100 = average, >100 = over-indexing)',
    category: 'Analytics',
  },
  Rematch: {
    term: 'Rematch',
    definition: 'Re-processing campaign attribution data',
    category: 'Operations',
  },
};

export const ATLAS_DATA_NOTE = 'Data has an 8-day processing lag. Use customer/campaign names or IDs for best results.';
